import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import {
  LayoutDashboard,
  CheckSquare,
  CheckCircle,
  Settings,
  Bell,
  LogOut,
  Sun,
  Moon,
  Monitor,
  X,
  Lightbulb,
  CalendarDays
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showTopBanner, setShowTopBanner] = useState(true);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15000);
    const onVisible = () => { if (document.visibilityState === 'visible') fetchNotifications(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API_URL}/notifications`);
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API_URL}/notifications/read-all`);
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Failed to mark notifications read:', error);
    }
  };

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/client/dashboard' },
    { icon: CheckSquare, label: 'Approvals', path: '/client/approvals' },
    { icon: CheckCircle, label: 'Approved', path: '/client/approved' },
    { icon: CalendarDays, label: 'Calendar', path: '/client/calendar' },
    { icon: Lightbulb, label: 'Ideas', path: '/client/suggestions' },
    { icon: Settings, label: 'Settings', path: '/client/settings' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#090e07] pb-20 md:pb-0">
      {/* Top Notification Banner */}
      {showTopBanner && unreadCount > 0 && (
        <div className="hidden md:flex fixed top-0 left-0 right-0 bg-[#556B2F] text-white z-40 px-6 py-2 items-center justify-between">
          <div className="flex items-center gap-3">
            <Bell className="w-4 h-4" />
            <span className="text-sm font-medium">
              You have {unreadCount} unread notification{unreadCount > 1 ? 's' : ''}
            </span>
            <Badge variant="secondary" className="bg-white/20 text-white hover:bg-white/30">
              {notifications[0]?.title || 'New activity'}
            </Badge>
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={markAllRead}
              className="text-sm underline hover:no-underline"
            >
              Mark all read
            </button>
            <button 
              onClick={() => setShowTopBanner(false)}
              className="p-1 hover:bg-white/20 rounded"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      
      {/* Desktop Header */}
      <header className={`hidden md:flex fixed left-0 right-0 h-16 bg-white dark:bg-[#0f1810] border-b border-slate-200 dark:border-[#1e2d18] z-30 px-6 items-center justify-between ${showTopBanner && unreadCount > 0 ? 'top-10' : 'top-0'}`}>
        <div className="flex items-center gap-3">
          <img src="/bda-logo.png" alt="BDA Technologies" className="h-8 w-auto" />
          <span className="text-lg font-bold text-slate-900 dark:text-white tracking-tight">ContentDesk</span>
        </div>

        <nav className="flex items-center gap-0.5 lg:gap-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase()}`}
              title={item.label}
              className={`flex items-center gap-2 px-3 lg:px-4 py-2 rounded-lg font-medium transition-colors ${
                isActive(item.path)
                  ? 'bg-[#556B2F] text-white'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-[#182213]'
              }`}
            >
              <item.icon className="w-4 h-4 shrink-0" />
              <span className="hidden lg:inline">{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {/* Notifications */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <div className="flex items-center justify-between px-4 py-2 border-b">
                <span className="font-semibold">Notifications</span>
                {unreadCount > 0 && (
                  <button onClick={markAllRead} className="text-sm text-[#556B2F] hover:underline">
                    Mark all read
                  </button>
                )}
              </div>
              <ScrollArea className="h-64">
                {notifications.length === 0 ? (
                  <div className="p-4 text-center text-slate-500">No notifications</div>
                ) : (
                  notifications.slice(0, 10).map((n) => (
                    <DropdownMenuItem key={n.id} className={`p-3 cursor-pointer ${!n.read ? 'bg-[#f0f4e8] dark:bg-[#556B2F]/10' : ''}`}>
                      <div>
                        <p className="font-medium text-sm">{n.title}</p>
                        <p className="text-xs text-slate-500 mt-1">{n.message}</p>
                      </div>
                    </DropdownMenuItem>
                  ))
                )}
              </ScrollArea>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Theme Toggle */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                {theme === 'dark' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setTheme('light')}>
                <Sun className="w-4 h-4 mr-2" /> Light
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTheme('dark')}>
                <Moon className="w-4 h-4 mr-2" /> Dark
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTheme('system')}>
                <Monitor className="w-4 h-4 mr-2" /> System
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-[#182213]" data-testid="user-menu">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#556B2F] to-[#556B2F] flex items-center justify-center text-white font-semibold text-sm">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5">
                <p className="font-medium text-sm">{user?.name}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-red-600" data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white/95 dark:bg-[#0f1810]/95 backdrop-blur-lg border-t border-slate-200 dark:border-[#1e2d18] z-30 safe-area-inset-bottom">
        <div className="flex items-stretch py-1.5">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`mobile-nav-${item.label.toLowerCase()}`}
              className={`flex flex-col items-center gap-0.5 flex-1 min-w-0 px-0.5 py-1 rounded-xl transition-colors ${
                isActive(item.path)
                  ? 'text-[#556B2F]'
                  : 'text-slate-500 dark:text-slate-400'
              }`}
            >
              <item.icon className="w-5 h-5 shrink-0" />
              <span className="text-[10px] font-medium truncate max-w-full">{item.label}</span>
            </Link>
          ))}

          {/* Notifications in Mobile */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex flex-col items-center gap-0.5 flex-1 min-w-0 px-0.5 py-1 rounded-xl text-slate-500 dark:text-slate-400" data-testid="mobile-notifications">
                <span className="relative shrink-0">
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1.5 -right-2 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </span>
                <span className="text-[10px] font-medium truncate max-w-full">Alerts</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="top" className="w-80 mb-2">
              <div className="flex items-center justify-between px-4 py-2 border-b">
                <span className="font-semibold">Notifications</span>
                {unreadCount > 0 && (
                  <button onClick={markAllRead} className="text-sm text-[#556B2F] hover:underline">
                    Mark all read
                  </button>
                )}
              </div>
              <ScrollArea className="h-64">
                {notifications.length === 0 ? (
                  <div className="p-4 text-center text-slate-500">No notifications</div>
                ) : (
                  notifications.slice(0, 10).map((n) => (
                    <DropdownMenuItem key={n.id} className={`p-3 ${!n.read ? 'bg-[#f0f4e8] dark:bg-[#556B2F]/10' : ''}`}>
                      <div>
                        <p className="font-medium text-sm">{n.title}</p>
                        <p className="text-xs text-slate-500 mt-1">{n.message}</p>
                      </div>
                    </DropdownMenuItem>
                  ))
                )}
              </ScrollArea>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </nav>

      {/* Main Content */}
      <main className={`md:pt-16 min-h-screen ${showTopBanner && unreadCount > 0 ? 'md:pt-[6.5rem]' : ''}`}>
        <div className="p-4 md:p-6 lg:p-8 max-w-4xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
};

export default ClientLayout;
